/**
 * The app reads the session-by-id composition branches on, named once.
 *
 * `useQuerySessionById` — the composed session read that BRANCHES between the
 * personal session query and the read-only team-member query — now lives in
 * `@heidi/session/data`. It cannot perform these reads itself: the router, an
 * app atom, two foreign `/data` doors, the app's CASL ability and its
 * authentication verdict are all things a headless data package may not
 * import. So they are resolved here and injected as ONE required
 * `configureSessionData` member (`src/utils/session-data-wiring.ts`), which is
 * ruling 32's disposition (a).
 *
 * THIS HOOK STAYS APP-SIDE BY DESIGN. It is not a shim awaiting a later move:
 * every value in it is app-shaped, and the package's business is what it does
 * with them, not where they come from.
 *
 * THE EXPRESSIONS ARE VERBATIM from the composition this replaces
 * (`src/hooks/query/useQuerySessionById.ts` and its two delegates) — including
 * the two DIFFERENT "reviewing another user" derivations, which is the one
 * thing in here worth reading twice. See `isReviewingOtherUserCohort` below.
 */
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useSessionPermissionCheck } from '@/hooks/useSessionPermissionCheck';
import { type SessionAccessContext } from '@heidi/session/data';
// The two workspace reads come from the DOOR, not from their `src/hooks/query`
// shims: this is a new file, and a new file naming a shim is a new legacy
// importer the workspace family ratchet counts (measured — it fails
// `pnpm migration:check` on `identity-projection`).
import { useCombinedQueriesTeamGeneralInfo, useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

export function useSessionAccessContext(sessionId: string): SessionAccessContext {
  const { pathname } = useRouter();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  // Unselected on purpose. The composition read `useQueryUser((d) =>
  // d.kinde_user_id)` while both of its delegates read the whole record
  // (`useQueryUser()` / `useIsReviewingOtherUser()`), so the wide subscription
  // was already unconditional on this render path; one call replaces three.
  const { data: userData } = useQueryUser();
  // The PROVIDER-AWARE verdict, not `!!token`. Both inner hooks moved off
  // token presence — a persisted token whose provider has settled logged-out
  // is NOT authenticated — and the composed read has to gate the same way or a
  // token-holding, signed-out user fires both session GETs.
  const { isAuthenticated } = useIsAuthenticated();
  const { checkPermission } = useSessionPermissionCheck(sessionId);

  const kindeUserId = userData?.kinde_user_id;
  const teamId = teamDetails?.team.id ?? '';
  const hasSharedSessionEditPermission = checkPermission(
    PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_EDIT,
    PERMISSION_SUBJECTS.SESSION_EDIT
  );

  // Memoized because the package hook reads the object itself: an identity
  // that churns every render would put a fresh argument object in front of
  // both inner queries on every render.
  return useMemo(
    () => ({
      isExternalLaunch: pathname === '/integration/epic/haiku/[...id]',
      isReviewingOtherUser:
        !!selectedReviewUser && !!teamId && selectedReviewUser !== kindeUserId,
      // `useIsReviewingOtherUser()`, inlined verbatim — its body is
      // `!selectedUser || !userData ? false : selectedUser !== kinde_user_id`,
      // and it lives in the `useQueryUser` app shim this file may not name.
      isReviewingOtherUserCohort:
        !!selectedReviewUser && !!userData && selectedReviewUser !== kindeUserId,
      teamId,
      memberId: selectedReviewUser ?? '',
      hasSharedSessionEditPermission,
      isAuthenticated,
      currentUserId: kindeUserId,
    }),
    [
      pathname,
      selectedReviewUser,
      teamId,
      userData,
      kindeUserId,
      hasSharedSessionEditPermission,
      isAuthenticated,
    ]
  );
}
